import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import type { DesignMode } from '@core-mkt/interfaces/brand-config';
import { facSmartPractice } from './smart-practice.icon';

export interface PillContent {
  label: string;
  /**
   * Class(es) applied to the pill button. Convention: a brand-skinnable name
   * (e.g. 'ace-mode-pill') paired with whatever Tailwind color utilities give it
   * its colorway. The matching modal frame is styled separately via `modalBodyClass`.
   */
  pillClasses: string;
  /** Leading icon; the component falls back to `faBolt` when omitted. */
  icon?: IconDefinition;
  /**
   * Optional brand allow-list (brandConfig.id). When set, the pill only renders on
   * those brands — a guard for pills that would otherwise leak onto another brand
   * sharing the same designMode (e.g. 'smart-practice' is DEC-only, but DEC and AARP
   * both run designMode 'default').
   */
  brandIds?: string[];
  /**
   * Class applied to the card root that contains this pill. Provides the
   * matching border styling and the positioning context so the pill can
   * overlap the card's top edge.
   */
  cardBorderClass: string;
  /**
   * Modal fields are optional and drive interactivity via `modalHeader`: pills WITH modal copy
   * open the info modal on click; pills WITHOUT it are non-interactive — `ProductPillComponent.
   * onPillClick` returns early when `modalHeader` is absent, so clicking/keyboard-activating the
   * pill does nothing (no empty modal). The `.smart-practice-pill` skin additionally sets
   * `pointer-events: none` so the badge reads as static. `modalBodyClass` is applied to the modal
   * body via VisionModal.bodyClass when the modal opens.
   */
  modalBodyClass?: string;
  modalHeader?: string;
  modalTitle?: string;
  modalBody?: string;
  designMode?: DesignMode;
}

export const PRODUCT_PILL_CONTENT: Record<string, PillContent> = {
  'ace-mode': {
    label: 'ACE MODE INCLUDED!',
    pillClasses: 'ace-mode-pill bg-ace-mode-linear text-vis-dark',
    modalBodyClass: 'ace-mode-pill-modal',
    cardBorderClass: 'ace-mode-pill-card',
    modalHeader: 'STUDY SMARTER WITH ACE MODE (FREE!)',
    modalTitle: 'Personalized permit test prep included with your course purchase.',
    modalBody: 'Ace Mode™ targets your toughest areas with personalized practice that sticks.',
    designMode: 'ace',
  },
  // DEC "Smart Practice" (EPS-4345). Gated to DEC (brandIds) on the 'default' designMode; DEC
  // authors select the `smart-practice` value on the Craft Product `productPills` field. Reuses
  // the Ace Mode pill CSS machinery (ace-mode-pill / ace-mode-pill-card / bg-ace-mode-linear +
  // the --ace-mode-pill-* tokens), recolored to the Smart Practice colorway in the DEC Tailwind
  // preset. Non-interactive badge: it has no modal copy, and the `smart-practice-pill` skin sets
  // `pointer-events: none` so the shared pill <button> is inert (clicking never opens a modal)
  // — see _product-pills.scss.
  'smart-practice': {
    label: 'SMART PRACTICE INCLUDED!',
    pillClasses: 'ace-mode-pill smart-practice-pill bg-ace-mode-linear text-vis-dark',
    icon: facSmartPractice,
    cardBorderClass: 'ace-mode-pill-card',
    designMode: 'default',
    brandIds: ['DEC'],
  },
};
